import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import {
  generate_tenant_invoice,
  generate_facility_invoice,
  get_leases,
  getUnitsForFacility,
  update_lease_status
} from "../../../../utils/urls";
import Layout from "../../component/layout";
import UpdateLease from "./edit_lease";
import PopulatedLeaseTemplate from "./lease_templates/populated_lease_template";
import TerminateLease from "./terminate_lease";
import UploadLeaseDocument from "./upload_lease_document";
import ViewLeaseDocuments from "./view_lease_documents";

const LeaseManagement = () => {
  const navigate = useNavigate();
  const [leases, setLeases] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [filteredLeases, setFilteredLeases] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedLeaseData, setSelectedLeaseData] = useState(null);
  const [isTemplateVisible, setIsTemplateVisible] = useState(false);
  const [selectedViewLeaseId, setSelectedViewLeaseId] = useState(null);

  // Document management state
  const [uploadDocumentDialogVisible, setUploadDocumentDialogVisible] = useState(false);
  const [viewDocumentsDialogVisible, setViewDocumentsDialogVisible] = useState(false);
  const [selectedDocumentLeaseId, setSelectedDocumentLeaseId] = useState(null);

  // Generate Invoice Modal State
  const [generateInvoiceVisible, setGenerateInvoiceVisible] = useState(false);
  const [selectedLeaseForInvoice, setSelectedLeaseForInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceFormData, setInvoiceFormData] = useState({
    generateYearMonth: "",
    customDueDate: null,
    isNewTenant: false,
    prorate: false,
    prorateFromDate: null,
    sendNotification: true
  });
  const [availableMonths, setAvailableMonths] = useState([]);
  const [invoiceErrors, setInvoiceErrors] = useState({});

  // Facility Billing State
  const [runBillingVisible, setRunBillingVisible] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [selectedBillingMonth, setSelectedBillingMonth] = useState("");
  const [sendBillingNotification, setSendBillingNotification] = useState(true);
  const [billingError, setBillingError] = useState("");

  const fetchLeases = useCallback(async () => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId) {
        toastify("No facility selected", "error");
        return;
      }

      const response = await makeRequest2(`${get_leases}/${facilityId}`, "GET");

      console.log("Leases response:", response);

      // Handle both old and new response formats
      const leaseData = response?.data?.leaseAgreements || response?.leaseAgreements;

      if (response?.success && leaseData && Array.isArray(leaseData)) {
        const processedLeases = leaseData.map(lease => ({
          ...lease,
          landlordName: lease.landlordInfo?.fullName || "N/A",
          tenantName: lease.tenantInfo?.fullName || "N/A",
          unitName: lease.unitInfo?.unitName || "N/A",
          leaseTemplateName: lease.leaseTemplateName || "N/A",
          leaseStatus: calculateLeaseStatus(lease),
          status: calculateLeaseStatus(lease),
          hasDocuments: lease.leaseDocuments && lease.leaseDocuments.length > 0,
          // Financial summary from backend
          totalAmount: lease.financialSummary?.totalAmount || 0,
          totalAmountWithEscalation: lease.financialSummary?.totalAmountWithEscalation || 0,
          currentRent: lease.financialSummary?.currentRent || 0,
          balanceBroughtForward: lease.financialSummary?.balanceBroughtForward || 0,
          hasActiveEscalations: lease.financialSummary?.hasEscalations || false
        }));

        setLeases(processedLeases);
        setFilteredLeases(processedLeases);
      } else {
        throw new Error('Failed to fetch leases');
      }
    } catch (error) {
      console.error('Error fetching leases:', error);
      toastify(error.message || "Error fetching leases", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleEdit = useCallback((lease) => {
    setSelectedLeaseData({
      ...lease,
      leaseTerms: {
        ...lease.leaseTerms,
        startDate: lease.leaseTerms?.startDate?.split('T')[0],
        endDate: lease.leaseTerms?.endDate?.split('T')[0]
      }
    });
    setEditDialogVisible(true);
  }, []);

  const handleUpdateSuccess = useCallback(async () => {
    await fetchLeases();
    setEditDialogVisible(false);
    setSelectedLeaseData(null);
  }, [fetchLeases]);

  const fetchUnitsForFacility = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getUnitsForFacility}/${facilityId}`,
        "GET"
      );
      if (response.success) {
        setUnits(response.data);
      } else {
        throw new Error("Failed to fetch units.");
      }
    } catch (error) {
      toastify(error.message, "error");
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    filterLeases(value, selectedStatus, selectedUnit);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    filterLeases(searchTerm, status, selectedUnit);
  };

  const handleUnitChange = (unit) => {
    setSelectedUnit(unit);
    filterLeases(searchTerm, selectedStatus, unit);
  };

  const filterLeases = (search, status, unit) => {
    let filtered = [...leases];

    if (search) {
      filtered = filtered.filter(lease =>
        lease.tenantName.toLowerCase().includes(search.toLowerCase()) ||
        lease.landlordName.toLowerCase().includes(search.toLowerCase()) ||
        lease.unitName.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (status) {
      filtered = filtered.filter(lease => lease.leaseStatus === status);
    }

    if (unit) {
      filtered = filtered.filter(lease => lease.unitName === unit);
    }

    setFilteredLeases(filtered);
  };

  const calculateLeaseStatus = (lease) => {
    if (lease.status === 'Terminated' || lease.status === 'Pending') {
      return lease.status;
    }

    if (isLeaseExpired(lease.leaseTerms.endDate) && lease.status !== 'Expired') {
      updateLeaseStatus(lease._id, 'Expired').catch(error => {
        console.error('Failed to update lease status:', error);
      });
      return 'Expired';
    }

    return lease.status || 'Active';
  };

  const isLeaseExpired = (endDate) => {
    return new Date(endDate) < new Date();
  };

  const updateLeaseStatus = async (leaseId, newStatus) => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId) {
        throw new Error("Facility ID not found");
      }

      const payload = {
        status: newStatus,
        leaseId: leaseId
      };

      const response = await makeRequest2(
        `${update_lease_status}/${facilityId}`,
        "PUT",
        payload
      );

      if (!response.success) {
        throw new Error(response.error || "Failed to update lease status");
      }

      return true;
    } catch (error) {
      console.error("Error updating lease status:", error);
      return false;
    }
  };

  // Generate Invoice Functions
  const handleGenerateInvoice = (lease) => {
    setSelectedLeaseForInvoice(lease);
    generateAvailableMonths(lease);
    resetInvoiceForm();
    setGenerateInvoiceVisible(true);
  };

  const resetInvoiceForm = () => {
    setInvoiceFormData({
      generateYearMonth: "",
      customDueDate: null,
      isNewTenant: false,
      prorate: false,
      prorateFromDate: null,
      sendNotification: true
    });
    setInvoiceErrors({});
  };

  const generateAvailableMonths = (leaseData) => {
    if (!leaseData) return;

    const leaseStartDate = new Date(leaseData.leaseTerms.startDate);
    const leaseEndDate = new Date(leaseData.leaseTerms.endDate);

    const months = [];
    const currentDate = new Date(leaseStartDate);

    // Allow billing up to 1 year in the future from today
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 1);
    const endDate = leaseEndDate < maxDate ? leaseEndDate : maxDate;

    while (currentDate <= endDate) {
      const year = currentDate.getFullYear();
      const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
      const yearMonth = `${year}-${month}`;

      months.push({
        label: `${currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
        value: yearMonth
      });

      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    setAvailableMonths(months);
  };

  const validateInvoiceForm = () => {
    const newErrors = {};

    if (!invoiceFormData.generateYearMonth) {
      newErrors.generateYearMonth = "Please select a month to generate invoice for";
    }

    if (invoiceFormData.prorate && !invoiceFormData.prorateFromDate) {
      newErrors.prorateFromDate = "Please select a proration start date";
    }

    if (invoiceFormData.prorateFromDate && invoiceFormData.generateYearMonth) {
      const prorateDate = new Date(invoiceFormData.prorateFromDate);
      const [year, month] = invoiceFormData.generateYearMonth.split('-').map(Number);
      const invoiceMonth = new Date(year, month - 1, 1);
      const invoiceMonthEnd = new Date(year, month, 0);

      if (prorateDate < invoiceMonth || prorateDate > invoiceMonthEnd) {
        newErrors.prorateFromDate = "Proration date must be within the selected invoice month";
      }
    }

    setInvoiceErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInvoiceSubmit = async () => {
    if (!validateInvoiceForm()) {
      return;
    }

    try {
      setInvoiceLoading(true);

      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId) {
        throw new Error("No facility selected");
      }

      const payload = {
        facilityId: facilityId,
        contractId: selectedLeaseForInvoice._id,
        contractType: "lease",
        options: {
          generateYearMonth: invoiceFormData.generateYearMonth,
          ...(invoiceFormData.customDueDate && {
            customDueDate: invoiceFormData.customDueDate.toISOString()
          }),
          ...(invoiceFormData.isNewTenant && {
            isNewTenant: true
          }),
          ...(invoiceFormData.prorate && invoiceFormData.prorateFromDate && {
            prorate: true,
            prorateFromDate: new Date(invoiceFormData.prorateFromDate.getFullYear(),
              invoiceFormData.prorateFromDate.getMonth(),
              invoiceFormData.prorateFromDate.getDate(), 12, 0, 0).toISOString()
          }),
          sendNotification: invoiceFormData.sendNotification
        }
      };

      console.log('Generating invoice with payload:', payload);

      const response = await makeRequest2(
        `${generate_tenant_invoice}`,
        "POST",
        payload
      );

      if (response.success) {
        toastify(`Invoice generated successfully`, "success");
        setGenerateInvoiceVisible(false);
        await fetchLeases(); // Refresh the lease list
      } else {
        throw new Error(response.message || 'Failed to generate invoice');
      }

    } catch (error) {
      console.error('Error generating invoice:', error);
      toastify(error.message || 'Error generating invoice', "error");
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleInvoiceInputChange = (field, value) => {
    setInvoiceFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (invoiceErrors[field]) {
      setInvoiceErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Facility Billing Functions
  const generateBillingMonths = () => {
    const months = [];
    const currentDate = new Date();

    // Generate months from 12 months ago to 12 months in the future (25 total months)
    for (let i = -12; i <= 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const yearMonth = `${year}-${month}`;

      months.push({
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        value: yearMonth
      });
    }

    return months;
  };

  const handleRunBilling = () => {
    setSelectedBillingMonth("");
    setSendBillingNotification(true);
    setBillingError("");
    setRunBillingVisible(true);
  };

  const handleBillingSubmit = async () => {
    if (!selectedBillingMonth) {
      setBillingError("Please select a month");
      return;
    }

    setBillingError("");
    setBillingLoading(true);

    try {
      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId) {
        throw new Error("No facility selected");
      }

      const payload = {
        invoiceTypes: ["LEASE"],
        priority: 8,
        delay: 0,
        generateYearMonth: selectedBillingMonth,
        sendNotification: sendBillingNotification
      };

      console.log('Triggering facility billing:', { facilityId, payload });

      const response = await makeRequest2(
        `${generate_facility_invoice}/${facilityId}`,
        "POST",
        payload
      );

      if (response.success || response.message) {
        toastify('Facility billing triggered successfully!', "success");
        setRunBillingVisible(false);
        setSelectedBillingMonth("");
        setSendBillingNotification(true);
        await fetchLeases();
      } else {
        throw new Error(response.error || 'Failed to trigger facility billing');
      }
    } catch (error) {
      console.error('Error triggering facility billing:', error);
      toastify(error.message || 'Error triggering facility billing', "error");
    } finally {
      setBillingLoading(false);
    }
  };

  const handleBillingCancel = () => {
    setSelectedBillingMonth("");
    setSendBillingNotification(true);
    setBillingError("");
    setRunBillingVisible(false);
  };

  const getMinDate = () => {
    if (!invoiceFormData.generateYearMonth) return new Date();

    const [year, month] = invoiceFormData.generateYearMonth.split('-').map(Number);
    return new Date(year, month - 1, 1);
  };

  const getMaxDate = () => {
    if (!invoiceFormData.generateYearMonth) return new Date();

    const [year, month] = invoiceFormData.generateYearMonth.split('-').map(Number);
    return new Date(year, month, 0);
  };

  const statusTemplate = (rowData) => {
    const statusConfig = {
      Active: { severity: 'success', value: 'Active' },
      Expired: { severity: 'danger', value: 'Expired' },
      Terminated: { severity: 'secondary', value: 'Terminated' },
      Pending: { severity: 'warning', value: 'Pending' }
    };

    const config = statusConfig[rowData.leaseStatus] || statusConfig.Active;

    return (
      <Tag
        severity={config.severity}
        value={config.value}
        style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
      />
    );
  };

  // Shows the rent amount only; any balance brought forward is noted separately
  const amountTemplate = (rowData) => {
    const currencyCode = rowData.currency?.currencyShortCode || '';
    const monthlyRent = Number(rowData.financialTerms?.monthlyRent || 0);
    const balanceBroughtForward = Number(rowData.balanceBroughtForward || 0);
    const hasEscalations = rowData.hasActiveEscalations;
    // currentRent is the (escalated) rent without the balance brought forward
    const rentAmount = Number(rowData.currentRent || monthlyRent);

    return (
      <div className="financial-summary">
        <div className="amount-main">
          <strong>{currencyCode} {rentAmount.toLocaleString()}</strong>
          {hasEscalations && (
            <Tag
              severity="info"
              value="Escalated"
              style={{ fontSize: '0.7rem', marginLeft: '0.5rem' }}
            />
          )}
        </div>
        {balanceBroughtForward !== 0 && (
          <small className="text-muted">
            Balance B/F: {currencyCode} {balanceBroughtForward.toLocaleString()}
          </small>
        )}
      </div>
    );
  };

  const documentTemplate = (rowData) => {
    const hasDocuments = rowData.leaseDocuments && rowData.leaseDocuments.length > 0;

    return (
      <div className="d-flex gap-2">
        {/* REMOVED THE STATUS CHECK - Upload button now available for all lease statuses */}
        <Button
          icon="ti ti-upload"
          className="p-button-rounded p-button-success p-button-sm"
          onClick={() => {
            setSelectedDocumentLeaseId(rowData._id);
            setUploadDocumentDialogVisible(true);
          }}
          tooltip="Upload Document"
        />

        <Button
          icon="ti ti-file"
          className={`p-button-rounded p-button-sm ${hasDocuments ? 'p-button-primary' : 'p-button-secondary'}`}
          onClick={() => {
            setSelectedDocumentLeaseId(rowData._id);
            setViewDocumentsDialogVisible(true);
          }}
          tooltip={hasDocuments ? `View Documents (${rowData.leaseDocuments?.length || 0})` : "No Documents"}
        />
      </div>
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="d-flex gap-2">
        <Button
          icon="ti ti-edit"
          className="p-button-rounded p-button-warning p-button-sm"
          onClick={() => handleEdit(rowData)}
          tooltip="Edit Lease"
        />
        <TerminateLease
          leaseId={rowData._id}
          initialStatus={rowData.status}
          fetchLeases={fetchLeases}
        />
        <Button
          icon="ti ti-eye"
          className="p-button-rounded p-button-info p-button-sm"
          onClick={() => {
            setSelectedViewLeaseId(rowData._id);
            setIsTemplateVisible(true);
          }}
          tooltip="View Lease Template"
        />
        {/* Generate Invoice Button - Only show for Active leases */}
        {rowData.leaseStatus === 'Active' && (
          <Button
            icon="ti ti-receipt"
            className="p-button-rounded p-button-success"
            onClick={() => handleGenerateInvoice(rowData)}
            tooltip="Generate Invoice"
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    fetchLeases();
    fetchUnitsForFacility();
  }, [fetchLeases]);

  const handleDocumentUploadSuccess = useCallback(async (leaseId) => {
    try {
      if (!leaseId) {
        console.error("No lease ID provided for status update");
        return;
      }

      // Only update status to 'Active' if the current status is 'Pending'
      const currentLease = leases.find(lease => lease._id === leaseId);
      if (currentLease && currentLease.leaseStatus === 'Pending') {
        try {
          await updateLeaseStatus(leaseId, 'Active');
          toastify("Document uploaded and lease status updated to Active", "success");
        } catch (statusError) {
          console.warn("Status update failed:", statusError);
          toastify("Document uploaded but failed to update lease status", "warning");
        }
      } else {
        toastify("Document uploaded successfully", "success");
      }
    } catch (error) {
      console.error("Error handling upload success:", error);
      toastify("Document uploaded but encountered an error", "warning");
    }

    await fetchLeases();
    setUploadDocumentDialogVisible(false);
    setSelectedDocumentLeaseId(null);
  }, [fetchLeases, leases]);

  // Generate Invoice Modal Footer
  const invoiceModalFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        type="button"
        className="btn btn-outline-secondary"
        onClick={() => setGenerateInvoiceVisible(false)}
        disabled={invoiceLoading}
      />
      <Button
        label="Generate Invoice"
        type="button"
        className="btn btn-primary"
        onClick={handleInvoiceSubmit}
        loading={invoiceLoading}
        disabled={!invoiceFormData.generateYearMonth}
      />
    </div>
  );

  // Run Billing Modal Footer
  const billingModalFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="btn btn-outline-secondary"
        onClick={handleBillingCancel}
        disabled={billingLoading}
      />
      <Button
        label="Run Billing"
        className="btn btn-primary"
        onClick={handleBillingSubmit}
        loading={billingLoading}
        disabled={!selectedBillingMonth}
      />
    </div>
  );

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="">Lease Management</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="">Leases</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5>Lease Agreements</h5>
            </div>
            <div className="card-body">
              {/* Filters */}
              <div className="row mb-3">
                <div className="col-md-3">
                  <input
                    className="form-control"
                    placeholder="Search leases..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <select
                    className="form-control"
                    value={selectedStatus}
                    onChange={(e) => handleStatusChange(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="Active">Active</option>
                    <option value="Pending">Pending</option>
                    <option value="Expired">Expired</option>
                    <option value="Terminated">Terminated</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-control"
                    value={selectedUnit}
                    onChange={(e) => handleUnitChange(e.target.value)}
                  >
                    <option value="">All Units</option>
                    {units.map((unit) => (
                      <option key={unit._id} value={unit.name}>
                        {unit.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 text-end">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      className="btn"
                      style={{ backgroundColor: '#0d6efd', color: 'white', border: 'none' }}
                      onClick={handleRunBilling}
                    >
                      Run Billing
                    </button>
                    <button
                      className="btn"
                      style={{ backgroundColor: '#0d6efd', color: 'white', border: 'none' }}
                      onClick={() => navigate("/facility/lease_management/create_lease")}
                    >
                      Add Lease
                    </button>
                  </div>
                </div>
              </div>

              <DataTable
                value={filteredLeases}
                paginator
                rows={10}
                loading={loading}
                emptyMessage="No leases found"
                sortMode="multiple"
                removableSort
                stripedRows
                tableStyle={{ minWidth: '70rem' }}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} leases"
                rowsPerPageOptions={[10, 25, 50]}
              >
                <Column
                  field="landlordName"
                  header="Landlord"
                  sortable
                  style={{ minWidth: '150px' }}
                />
                <Column
                  field="tenantName"
                  header="Tenant"
                  sortable
                  style={{ minWidth: '150px' }}
                />
                <Column
                  field="unitName"
                  header="Unit"
                  sortable
                  style={{ minWidth: '100px' }}
                />
                <Column
                  field="leaseTerms.startDate"
                  header="Start Date"
                  sortable
                  style={{ minWidth: '120px' }}
                  body={(rowData) => new Date(rowData.leaseTerms.startDate).toLocaleDateString()}
                />
                <Column
                  field="leaseTerms.endDate"
                  header="End Date"
                  sortable
                  style={{ minWidth: '120px' }}
                  body={(rowData) => new Date(rowData.leaseTerms.endDate).toLocaleDateString()}
                />
                <Column
                  field="totalAmount"
                  header="Rent Amount"
                  sortable
                  style={{ minWidth: '180px' }}
                  body={amountTemplate}
                />

                <Column
                  field="financialTerms.digitax.synced"
                  header="Digitax sync"
                  style={{ minWidth: '180px' }}
                />

                <Column
                  field="leaseStatus"
                  header="Status"
                  body={statusTemplate}
                  sortable
                  style={{ minWidth: '100px' }}
                />

                <Column
                  header="Documents"
                  body={documentTemplate}
                  style={{ minWidth: '120px' }}
                />
                <Column
                  header="Actions"
                  body={actionBodyTemplate}
                  style={{ minWidth: '150px' }}
                />
              </DataTable>
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <UpdateLease
        visible={editDialogVisible}
        onHide={() => {
          setEditDialogVisible(false);
          setSelectedLeaseData(null);
        }}
        leaseData={selectedLeaseData}
        onUpdate={handleUpdateSuccess}
      />

      <PopulatedLeaseTemplate
        leaseId={selectedViewLeaseId}
        visible={isTemplateVisible}
        onHide={() => {
          setIsTemplateVisible(false);
          setSelectedViewLeaseId(null);
        }}
      />

      <UploadLeaseDocument
        leaseId={selectedDocumentLeaseId}
        visible={uploadDocumentDialogVisible}
        onHide={() => {
          setUploadDocumentDialogVisible(false);
          setSelectedDocumentLeaseId(null);
        }}
        onUploadSuccess={() => handleDocumentUploadSuccess(selectedDocumentLeaseId)}
      />

      <ViewLeaseDocuments
        leaseId={selectedDocumentLeaseId}
        visible={viewDocumentsDialogVisible}
        onHide={() => {
          setViewDocumentsDialogVisible(false);
          setSelectedDocumentLeaseId(null);
        }}
      />

      {/* Generate Invoice Dialog */}
      <Dialog
        visible={generateInvoiceVisible}
        onHide={() => setGenerateInvoiceVisible(false)}
        header="Generate Invoice"
        footer={invoiceModalFooter}
        style={{ width: '500px' }}
        modal
      >
        <div className="grid p-fluid">
          {/* Lease Information */}
          <div className="col-12 mb-3">
            <div className="p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <h6 className="mb-2">Lease Information</h6>
              <div className="grid">
                <div className="col-6">
                  <strong>Tenant:</strong> {selectedLeaseForInvoice?.tenantName || 'N/A'}
                </div>
                <div className="col-6">
                  <strong>Unit:</strong> {selectedLeaseForInvoice?.unitName || 'N/A'}
                </div>
                <div className="col-6">
                  <strong>Monthly Rent:</strong> {selectedLeaseForInvoice?.currency?.currencyShortCode} {selectedLeaseForInvoice?.financialTerms?.monthlyRent?.toLocaleString()}
                </div>
                <div className="col-6">
                  <strong>Last Invoice:</strong> {selectedLeaseForInvoice?.lastInvoiceYearMonth || 'None'}
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Month Selection */}
          <div className="col-12">
            <label htmlFor="generateYearMonth" className="font-bold">
              Invoice Month <span className="text-red-500">*</span>
            </label>
            <Dropdown
              id="generateYearMonth"
              value={invoiceFormData.generateYearMonth}
              options={availableMonths}
              onChange={(e) => handleInvoiceInputChange('generateYearMonth', e.value)}
              placeholder="Select month to generate invoice for"
              className={invoiceErrors.generateYearMonth ? 'p-invalid' : ''}
            />
            {invoiceErrors.generateYearMonth && (
              <small className="p-error">{invoiceErrors.generateYearMonth}</small>
            )}
          </div>

          {/* Custom Due Date */}
          <div className="col-12">
            <label htmlFor="customDueDate" className="font-bold">
              Custom Due Date (Optional)
            </label>
            <Calendar
              id="customDueDate"
              value={invoiceFormData.customDueDate}
              onChange={(e) => handleInvoiceInputChange('customDueDate', e.value)}
              placeholder="Select custom due date"
              showIcon
              dateFormat="dd/mm/yy"
            />
            <small className="text-gray-600">
              Leave empty to use default due date from lease terms
            </small>
          </div>

          {/* New Tenant Toggle */}
          <div className="col-12">
            <div className="flex align-items-center">
              <InputSwitch
                id="isNewTenant"
                checked={invoiceFormData.isNewTenant}
                onChange={(e) => handleInvoiceInputChange('isNewTenant', e.value)}
              />
              <label htmlFor="isNewTenant" className="ml-2 font-bold">
                New Tenant
              </label>
            </div>
            <small className="text-gray-600">
              Enable if this is the first invoice for a new tenant
            </small>
          </div>

          {/* Proration Section */}
          <div className="col-12">
            <div className="flex align-items-center mb-2">
              <InputSwitch
                id="prorate"
                checked={invoiceFormData.prorate}
                onChange={(e) => handleInvoiceInputChange('prorate', e.value)}
              />
              <label htmlFor="prorate" className="ml-2 font-bold">
                Prorate Invoice
              </label>
            </div>
            <small className="text-gray-600">
              Enable to calculate partial month charges
            </small>
          </div>

          {/* Proration Date */}
          {invoiceFormData.prorate && (
            <div className="col-12">
              <label htmlFor="prorateFromDate" className="font-bold">
                Proration Start Date <span className="text-red-500">*</span>
              </label>
              <Calendar
                id="prorateFromDate"
                value={invoiceFormData.prorateFromDate}
                onChange={(e) => handleInvoiceInputChange('prorateFromDate', e.value)}
                placeholder="Select proration start date"
                showIcon
                dateFormat="dd/mm/yy"
                minDate={getMinDate()}
                maxDate={getMaxDate()}
                className={invoiceErrors.prorateFromDate ? 'p-invalid' : ''}
              />
              {invoiceErrors.prorateFromDate && (
                <small className="p-error">{invoiceErrors.prorateFromDate}</small>
              )}
              <small className="text-gray-600">
                Date from which to calculate prorated charges
              </small>
            </div>
          )}

          {/* Notification Toggle */}
          <div className="col-12">
            <div className="flex align-items-center">
              <InputSwitch
                id="sendNotification"
                checked={invoiceFormData.sendNotification}
                onChange={(e) => handleInvoiceInputChange('sendNotification', e.value)}
              />
              <label htmlFor="sendNotification" className="ml-2 font-bold">
                Send Notification
              </label>
            </div>
            <small className="text-gray-600">
              Send invoice notification to tenant
            </small>
          </div>

          {/* Warning Message */}
          {availableMonths.length === 0 && (
            <div className="col-12">
              <Message
                severity="warn"
                text="No available months for invoice generation. The lease may be up to date or expired."
              />
            </div>
          )}
        </div>
      </Dialog>

      {/* Run Billing Dialog */}
      <Dialog
        visible={runBillingVisible}
        onHide={handleBillingCancel}
        header="Run Facility Billing"
        footer={billingModalFooter}
        style={{ width: '500px' }}
        modal
      >
        <div className="grid p-fluid">
          <div className="col-12 mb-3">
            <Message
              severity="info"
              text="Generate invoices for all active lease contracts in this facility for the selected month."
              style={{ width: '100%' }}
            />
          </div>

          <div className="col-12">
            <label htmlFor="billingMonth" className="font-bold mb-2">
              Billing Month <span style={{ color: 'red' }}>*</span>
            </label>
            <Dropdown
              id="billingMonth"
              value={selectedBillingMonth}
              options={generateBillingMonths()}
              onChange={(e) => {
                setSelectedBillingMonth(e.value);
                setBillingError("");
              }}
              placeholder="Select month to run billing"
              className={billingError ? 'p-invalid' : ''}
            />
            {billingError && (
              <small style={{ color: 'red', marginTop: '4px', display: 'block' }}>
                {billingError}
              </small>
            )}
            <small style={{ color: '#6c757d', marginTop: '8px', display: 'block' }}>
              The system will automatically generate invoices for all eligible leases
            </small>
          </div>

          <div className="col-12 mt-3">
            <div className="flex align-items-center">
              <InputSwitch
                id="sendBillingNotification"
                checked={sendBillingNotification}
                onChange={(e) => setSendBillingNotification(e.value)}
              />
              <label htmlFor="sendBillingNotification" className="ml-2 font-bold">
                Send Notifications
              </label>
            </div>
            <small style={{ color: '#6c757d', marginTop: '4px', display: 'block' }}>
              Automatically send invoice notifications to all tenants
            </small>
          </div>

          <div className="col-12 mt-3">
            <div className="p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <h6 className="mb-2">What happens next?</h6>
              <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                <li>Invoices will be generated for all active lease contracts</li>
                <li>The system will skip leases that already have invoices for this month</li>
                {sendBillingNotification && <li>Notifications will be sent to tenants automatically</li>}
                {!sendBillingNotification && <li>No notifications will be sent (you can send them manually later)</li>}
                <li>You can track the progress in the invoices section</li>
              </ul>
            </div>
          </div>
        </div>
      </Dialog>
    </Layout>
  );
};

export default LeaseManagement;